// Smart Sharpen filter — sharpens with edge-aware noise reduction
// Combines unsharp masking with luminance-based thresholding to avoid amplifying noise.

import type { Filter } from "./Filter";
import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";

export interface SmartSharpenParams {
  amount: number;
  radius: number;
  reduceNoise: number;
  threshold: number;
  remove: string;
}

export class SmartSharpen implements Filter<SmartSharpenParams> {
  readonly id = "smart-sharpen";
  readonly displayName = "Smart Sharpen";
  readonly defaultParams: SmartSharpenParams = { amount: 150, radius: 1, reduceNoise: 0, threshold: 0, remove: "gaussian-blur" };

  apply(src: PixelBuffer, params: SmartSharpenParams, _selection: Selection | null): PixelBuffer {
    const { width, height } = src;
    const dst = new PixelBuffer(width, height, "rgba8");
    const data = src.data;
    const out = dst.data;
    const amount = params.amount / 100;
    const radius = Math.max(0.1, params.radius);
    const noiseReduction = params.reduceNoise / 100;
    const threshold = params.threshold;

    const blurred = gaussianBlur(src, radius);
    const blurData = blurred.data;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const di = (y * width + x) * 4;

        const lum = data[di]! * 0.299 + data[di + 1]! * 0.587 + data[di + 2]! * 0.114;
        const blurLum = blurData[di]! * 0.299 + blurData[di + 1]! * 0.587 + blurData[di + 2]! * 0.114;
        const diff = lum - blurLum;

        if (Math.abs(diff) < threshold) {
          out[di] = data[di]!;
          out[di + 1] = data[di + 1]!;
          out[di + 2] = data[di + 2]!;
          out[di + 3] = data[di + 3]!;
          continue;
        }

        for (let c = 0; c < 3; c++) {
          const sharpened = data[di + c]! + amount * (data[di + c]! - blurData[di + c]!);
          const noiseReduced = data[di + c]! + amount * 0.5 * (data[di + c]! - blurData[di + c]!);
          out[di + c] = Math.min(255, Math.max(0, Math.round(
            sharpened * (1 - noiseReduction) + noiseReduced * noiseReduction
          )));
        }
        out[di + 3] = data[di + 3]!;
      }
    }
    dst.markDirty();
    return dst;
  }
}

function gaussianBlur(src: PixelBuffer, radius: number): PixelBuffer {
  const { width, height } = src;
  const dst = new PixelBuffer(width, height, "rgba8");
  const data = src.data;
  const out = dst.data;
  const r = Math.max(1, Math.ceil(radius));
  const sigma = radius / 2;
  const weights = new Float32Array(r + 1);
  let weightSum = 0;
  for (let i = 0; i <= r; i++) {
    const w = Math.exp(-(i * i) / (2 * sigma * sigma));
    weights[i] = w;
    weightSum += w * (i === 0 ? 1 : 2);
  }
  for (let i = 0; i <= r; i++) {
    const w = weights[i]!;
    weights[i] = w / weightSum;
  }

  const tmp = new Uint8ClampedArray(data);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const di = (y * width + x) * 4;
      for (let c = 0; c < 4; c++) {
        let sum = 0;
        for (let dx = -r; dx <= r; dx++) {
          const nx = Math.min(width - 1, Math.max(0, x + dx));
          const ni = (y * width + nx) * 4 + c;
          const val = tmp[ni];
          if (val !== undefined) sum += val * weights[Math.abs(dx)]!;
        }
        out[di + c] = Math.round(sum);
      }
    }
  }

  const tmp2 = new Uint8ClampedArray(out);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const di = (y * width + x) * 4;
      for (let c = 0; c < 4; c++) {
        let sum = 0;
        for (let dy = -r; dy <= r; dy++) {
          const ny = Math.min(height - 1, Math.max(0, y + dy));
          const ni = (ny * width + x) * 4 + c;
          const val = tmp2[ni];
          if (val !== undefined) sum += val * weights[Math.abs(dy)]!;
        }
        out[di + c] = Math.round(sum);
      }
    }
  }
  dst.markDirty();
  return dst;
}
